import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, CheckCircle2, ChevronLeft, ChevronRight, CircleSlash, Clock3, Filter, Forward,
  History, Inbox, Loader2, Lock, Route, Search, Send, ShieldCheck, ThumbsDown, TimerOff,
  Undo2, UserCheck, UserRound, Wrench, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ForwardLeadDialog } from "@/components/common/ForwardLeadDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const STATUS_STYLES = {
  Pending: "bg-sky-50 text-sky-700 border-sky-200",
  "Awaiting Allocation": "bg-amber-50 text-amber-700 border-amber-200",
  Accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Expired: "bg-slate-100 text-slate-500 border-slate-200",
  Withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const EMPTY_FILTERS = { status: "all", company_id: "all", reason: "all", recruiter_id: "all", q: "", date_from: "", date_to: "", sort: "newest" };
const PAGE_SIZE = 20;

export default function ForwardLeads() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState(isAdmin ? "Pending" : "pool");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ reasons: [], statuses: [] });
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);

  // dialogs
  const [fwdOpen, setFwdOpen] = useState(false);
  const [acceptOf, setAcceptOf] = useState(null);
  const [rejectOf, setRejectOf] = useState(null);
  const [withdrawOf, setWithdrawOf] = useState(null);
  const [assignOf, setAssignOf] = useState(null);
  const [assignRid, setAssignRid] = useState("");
  const [closeOf, setCloseOf] = useState(null);
  const [expireOf, setExpireOf] = useState(null);
  const [reopenOf, setReopenOf] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detailOf, setDetailOf] = useState(null);
  const [detail, setDetail] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [timelineOf, setTimelineOf] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.get("/forward-leads/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/companies", { params: { limit: 500 } })
      .then((r) => setCompanies(r.data.items || r.data || [])).catch(() => {});
    if (isAdmin) api.get("/users/recruiters").then((r) => setRecruiters(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(() => {
    setError(false);
    const params = { page, page_size: PAGE_SIZE, sort: filters.sort };
    if (isAdmin) {
      if (tab !== "all") params.status = tab;
      if (filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    } else {
      params.view = tab;
    }
    if (filters.status !== "all" && (isAdmin ? tab === "all" : true)) params.status = filters.status;
    if (filters.company_id !== "all") params.company_id = filters.company_id;
    if (filters.reason !== "all") params.reason = filters.reason;
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const calls = [api.get("/forward-leads", { params }), api.get("/forward-leads/kpis")];
    if (isAdmin) calls.push(api.get("/forward-leads/reports"));
    Promise.all(calls)
      .then(([l, k, r]) => {
        setItems(l.data.items);
        setTotal(l.data.total);
        setKpis(k.data);
        if (r) setReports(r.data);
        setSelected([]);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab, filters, page, isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => setPage(1), [tab, filters]);

  // ---------------------------------------------------------------- actions
  const runOwnershipRepair = async () => {
    if (!window.confirm(
      "Run the one-time ownership repair?\n\nThis clears any stale 'forwarded' status left on already-accepted leads (so the new owner's action buttons show) and syncs the Candidate Directory owner to the recruiter who accepted. Safe to re-run."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/forward-leads/repair/ownership-sync");
      toast.success(
        `Repair complete — ${data.apps_count} application(s) unblocked, ${data.candidates_count} candidate owner(s) synced`,
        { duration: 8000, description: data.apps_count + data.candidates_count === 0 ? "Nothing needed fixing." : undefined }
      );
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (fn, done) => {
    setBusy(true);
    try {
      await fn();
      done && done();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Phase 36: creation handled by the single shared ForwardLeadDialog (picker mode).
  const openForward = () => setFwdOpen(true);

  const submitAccept = () => doAction(async () => {
    const res = await api.post(`/forward-leads/${acceptOf.id}/accept`);
    const t = res.data.ownership_transfer;
    toast.success(`Lead accepted — ownership transferred${t?.applications_transferred?.length ? ` (${t.applications_transferred.length} application(s))` : ""}`);
  }, () => setAcceptOf(null));

  const submitReject = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "A rejection reason is mandatory" } } };
    await api.post(`/forward-leads/${rejectOf.id}/reject`, { reason: reason.trim() });
    toast.success("Lead rejected");
  }, () => { setRejectOf(null); setReason(""); });

  const submitWithdraw = () => doAction(async () => {
    await api.post(`/forward-leads/${withdrawOf.id}/withdraw`, { remarks: reason || null });
    toast.success("Lead withdrawn");
  }, () => { setWithdrawOf(null); setReason(""); });

  const submitAssign = () => doAction(async () => {
    if (!assignRid) throw { response: { data: { detail: "Choose a recruiter" } } };
    await api.post(`/forward-leads/${assignOf.id}/assign`, { recruiter_id: assignRid });
    toast.success("Lead assigned — now visible to that recruiter only");
  }, () => { setAssignOf(null); setAssignRid(""); });

  const submitClose = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "A close reason is mandatory" } } };
    await api.post(`/forward-leads/${closeOf.id}/close`, { reason: reason.trim() });
    toast.success("Lead closed");
  }, () => { setCloseOf(null); setReason(""); });

  const submitExpire = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "An expiry remark is mandatory" } } };
    await api.post(`/forward-leads/${expireOf.id}/expire`, { reason: reason.trim() });
    toast.success("Lead marked Expired");
  }, () => { setExpireOf(null); setReason(""); });

  const submitReopen = () => doAction(async () => {
    const res = await api.post(`/forward-leads/${reopenOf.id}/reopen`, { remarks: reason || null });
    toast.success(`Lead reopened (${res.data.status})`);
  }, () => { setReopenOf(null); setReason(""); });

  const submitBulkClose = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "A close reason is mandatory" } } };
    const res = await api.post("/forward-leads/bulk-close", { ids: selected, reason: reason.trim() });
    toast.success(`${res.data.count} lead(s) closed`);
  }, () => { setBulkOpen(false); setReason(""); });

  const openDetail = (l) => {
    setDetailOf(l);
    setDetail(null);
    setSubLoading(true);
    api.get(`/forward-leads/${l.id}`)
      .then((r) => setDetail(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setSubLoading(false));
  };

  const openHistory = (l) => {
    setHistoryOf(l);
    setSubLoading(true);
    api.get(`/forward-leads/${l.id}/history`)
      .then((r) => setHistory(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setSubLoading(false));
  };

  const openTimeline = (l) => {
    setTimelineOf(l);
    setTimeline(null);
    setSubLoading(true);
    api.get(`/forward-leads/${l.id}/timeline`)
      .then((r) => setTimeline(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setSubLoading(false));
  };

  const canAccept = (l) => !isAdmin && l.status === "Pending" && tab === "pool";
  const canWithdraw = (l) => (l.status === "Pending" || l.status === "Awaiting Allocation")
    && (isAdmin || l.from_recruiter_id === user.id || l.created_by === user.id);
  const filtersActive = filters.status !== "all" || filters.company_id !== "all" || filters.reason !== "all" ||
    filters.recruiter_id !== "all" || filters.q || filters.date_from || filters.date_to;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Forward Leads</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Forward candidates to recruiters allocated to other companies — with full ownership audit.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <Button variant="outline" onClick={runOwnershipRepair} disabled={busy}
              className="rounded-lg bg-white" data-testid="fwd-repair-ownership-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wrench className="h-4 w-4 mr-2" />}
              Repair Ownership
            </Button>
          )}
          <Button onClick={openForward} className="rounded-lg" data-testid="forward-create-button">
            <Forward className="h-4 w-4 mr-2" /> Forward Candidate
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      {kpis && (isAdmin ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KpiCard label="Pending" value={kpis.pending} icon={Inbox} tone="primary" testId="fwd-kpi-pending" />
          <KpiCard label="Awaiting Allocation" value={kpis.awaiting_allocation} icon={Clock3} tone="warning" testId="fwd-kpi-awaiting" />
          <KpiCard label="Accepted" value={kpis.accepted} icon={CheckCircle2} tone="success" testId="fwd-kpi-accepted" />
          <KpiCard label="Rejected" value={kpis.rejected} icon={ThumbsDown} tone="warning" testId="fwd-kpi-rejected" />
          <KpiCard label="Success %" value={`${kpis.success_pct}%`} icon={ShieldCheck} tone="primary" testId="fwd-kpi-success" />
          <KpiCard label="Avg Acceptance (hrs)" value={kpis.avg_acceptance_hours} icon={Clock3} tone="slate" testId="fwd-kpi-avg-accept" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard label="Pool — Pending Leads" value={kpis.pool_pending} icon={Inbox} tone="primary" testId="fwd-kpi-pool" />
          <KpiCard label="My Forwards" value={kpis.my_forwards} icon={Send} tone="slate" testId="fwd-kpi-my-forwards" />
          <KpiCard label="Accepted by Me" value={kpis.accepted_by_me} icon={CheckCircle2} tone="success" testId="fwd-kpi-accepted-me" />
          <KpiCard label="Rejected by Me" value={kpis.rejected_by_me} icon={ThumbsDown} tone="warning" testId="fwd-kpi-rejected-me" />
          <KpiCard label="Avg Response (hrs)" value={kpis.avg_response_hours} icon={Clock3} tone="slate" testId="fwd-kpi-avg-response" />
        </div>
      ))}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg flex-wrap h-auto">
          {isAdmin ? (
            <>
              <TabsTrigger value="Pending" className="rounded-md" data-testid="fwd-tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="Awaiting Allocation" className="rounded-md" data-testid="fwd-tab-awaiting">Awaiting Allocation</TabsTrigger>
              <TabsTrigger value="Accepted" className="rounded-md" data-testid="fwd-tab-accepted">Accepted</TabsTrigger>
              <TabsTrigger value="Rejected" className="rounded-md" data-testid="fwd-tab-rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all" className="rounded-md" data-testid="fwd-tab-all">All</TabsTrigger>
            </>
          ) : (
            <>
              <TabsTrigger value="pool" className="rounded-md" data-testid="fwd-tab-pool">Pool</TabsTrigger>
              <TabsTrigger value="my_forwards" className="rounded-md" data-testid="fwd-tab-my-forwards">My Forwards</TabsTrigger>
              <TabsTrigger value="accepted" className="rounded-md" data-testid="fwd-tab-accepted-me">Accepted</TabsTrigger>
              <TabsTrigger value="rejected" className="rounded-md" data-testid="fwd-tab-rejected-me">Rejected</TabsTrigger>
            </>
          )}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="rounded-xl border-border shadow-none" data-testid="fwd-filters">
        <CardContent className="p-4">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0 xl:pb-2.5">
              <Filter className="h-4 w-4" /> Filters
            </div>
            <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-7" : "lg:grid-cols-6"} gap-3 flex-1`}>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Search candidate</Label>
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="bg-white h-9 pl-8" placeholder="Name…" value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="fwd-filter-search" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Company</Label>
                <Select value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="fwd-filter-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Reason</Label>
                <Select value={filters.reason} onValueChange={(v) => setFilters({ ...filters, reason: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="fwd-filter-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All reasons</SelectItem>
                    {options.reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Recruiter</Label>
                  <Select value={filters.recruiter_id} onValueChange={(v) => setFilters({ ...filters, recruiter_id: v })}>
                    <SelectTrigger className="bg-white h-9" data-testid="fwd-filter-recruiter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All recruiters</SelectItem>
                      {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="fwd-filter-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="fwd-filter-to" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Sort</Label>
                <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="fwd-filter-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="rounded-lg shrink-0" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="fwd-filter-reset">
                <X className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk bar (admin) */}
      {isAdmin && selected.length > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="fwd-bulk-bar">
          <p className="text-sm font-medium">{selected.length} lead(s) selected</p>
          <Button size="sm" variant="destructive" className="rounded-lg" onClick={() => { setBulkOpen(true); setReason(""); }} data-testid="fwd-bulk-close-button">
            <CircleSlash className="h-3.5 w-3.5 mr-1.5" /> Bulk Close
          </Button>
        </div>
      )}

      {/* Table */}
      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="fwd-error">
          <p className="text-sm text-muted-foreground">Could not load forward leads.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={Forward} title="No forward leads here"
          description={tab === "pool" ? "Leads forwarded to your allocated companies will appear here." : "Forward a candidate to another company to get started."} />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="w-8"></TableHead>}
                  <TableHead>Lead ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Target Company</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Forwarded By</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} className="hover:bg-muted/60" data-testid="fwd-row">
                    {isAdmin && (
                      <TableCell>
                        {(l.status === "Pending" || l.status === "Awaiting Allocation") && (
                          <Checkbox checked={selected.includes(l.id)}
                            onCheckedChange={() => setSelected((p) => p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id])}
                            data-testid={`fwd-select-${l.id}`} />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs">
                      <button className="hover:text-primary hover:underline" onClick={() => openDetail(l)} data-testid={`fwd-detail-${l.id}`}>
                        {l.fwd_code}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {l.masked && <Lock className="h-3 w-3 text-amber-500" title="Details unlock after acceptance" />}
                        {l.candidate_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{l.company_name}</span>
                    </TableCell>
                    <TableCell className="text-sm">{l.job_title || <span className="text-muted-foreground">Any suitable</span>}</TableCell>
                    <TableCell className="text-sm">{l.reason}</TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3 text-muted-foreground" />{l.from_recruiter_name || l.created_by_name || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formatDate(l.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full whitespace-nowrap ${STATUS_STYLES[l.status] || ""}`} data-testid="fwd-status-badge">
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {canAccept(l) && (
                          <Button size="sm" className="h-8 rounded-lg" onClick={() => setAcceptOf(l)} data-testid={`fwd-accept-${l.id}`}>
                            <UserCheck className="h-3.5 w-3.5 mr-1.5" /> Accept
                          </Button>
                        )}
                        {canAccept(l) && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-destructive hover:text-destructive"
                            onClick={() => { setRejectOf(l); setReason(""); }} data-testid={`fwd-reject-${l.id}`} title="Reject">
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canWithdraw(l) && !isAdmin && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                            onClick={() => { setWithdrawOf(l); setReason(""); }} data-testid={`fwd-withdraw-${l.id}`} title="Withdraw">
                            <Undo2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && (l.status === "Awaiting Allocation" || l.status === "Pending") && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                            onClick={() => { setAssignOf(l); setAssignRid(""); }} data-testid={`fwd-assign-${l.id}`}
                            title={l.status === "Awaiting Allocation" ? "Assign recruiter" : "Reassign"}>
                            <UserCheck className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && (l.status === "Pending" || l.status === "Awaiting Allocation") && (
                          <>
                            <Button size="sm" variant="ghost" className="h-8 rounded-lg" title="Mark Expired"
                              onClick={() => { setExpireOf(l); setReason(""); }} data-testid={`fwd-expire-${l.id}`}>
                              <TimerOff className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 rounded-lg text-destructive hover:text-destructive"
                              onClick={() => { setCloseOf(l); setReason(""); }} data-testid={`fwd-close-${l.id}`} title="Close">
                              <CircleSlash className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {isAdmin && l.status === "Expired" && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                            onClick={() => { setReopenOf(l); setReason(""); }} data-testid={`fwd-reopen-${l.id}`}>
                            <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Reopen
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openTimeline(l)}
                          data-testid={`fwd-timeline-${l.id}`} title="Candidate timeline">
                          <Route className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openHistory(l)}
                          data-testid={`fwd-history-${l.id}`} title="History">
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
            <p className="text-xs text-muted-foreground" data-testid="fwd-pagination-info">
              {total} lead(s) · page {page} of {pages}
            </p>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)} data-testid="fwd-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)} data-testid="fwd-page-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </Card>
      )}

      {/* Admin reports */}
      {isAdmin && reports && (
        <div className="space-y-4" data-testid="fwd-reports">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              ["Acceptance %", `${reports.totals.acceptance_pct}%`, `${reports.totals.accepted} of ${reports.totals.total}`, "report-acceptance-pct"],
              ["Forward → Lineup", `${reports.conversion.to_lineup_pct}%`, `${reports.conversion.to_lineup} of ${reports.conversion.accepted}`, "report-lineup-pct"],
              ["Forward → Interview", `${reports.conversion.to_interview_pct}%`, `${reports.conversion.to_interview} of ${reports.conversion.accepted}`, "report-interview-pct"],
              ["Forward → Joining", `${reports.conversion.to_joining_pct}%`, `${reports.conversion.to_joining} of ${reports.conversion.accepted}`, "report-joining-pct"],
              ["Forward → Billing", `${reports.conversion.to_billing_pct}%`, `${reports.conversion.to_billing} of ${reports.conversion.accepted}`, "report-billing-pct"],
            ].map(([label, value, sub, tid]) => (
              <Card key={tid} className="rounded-xl border-border shadow-none"><CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-semibold mt-1" data-testid={tid}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
              </CardContent></Card>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Company-wise</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.by_company.length === 0 && <p className="text-sm text-muted-foreground">No leads yet.</p>}
                {reports.by_company.map((r) => (
                  <div key={r.company} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 gap-2" data-testid="report-company-row">
                    <span className="text-sm truncate">{r.company}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{r.total} · A {r.accepted} · R {r.rejected} · {r.acceptance_pct}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Top forwarding recruiters</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.top_forwarding.length === 0 && <p className="text-sm text-muted-foreground">No forwards yet.</p>}
                {reports.top_forwarding.map((r) => (
                  <div key={r.recruiter} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 gap-2" data-testid="report-forwarding-row">
                    <span className="text-sm truncate">{r.recruiter}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">sent {r.sent} · accepted {r.accepted} · {r.success_pct}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Top receiving recruiters</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.top_receiving.length === 0 && <p className="text-sm text-muted-foreground">No accepted leads yet.</p>}
                {reports.top_receiving.map((r) => (
                  <div key={r.recruiter} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 gap-2" data-testid="report-receiving-row">
                    <span className="text-sm truncate">{r.recruiter}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">received {r.received}</span>
                  </div>
                ))}
                <div className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground" data-testid="report-rejected-by-all">
                  Rejected by all recruiters: {reports.totals.rejected_by_all} · Re-forwarded after rejection: {reports.totals.reforwarded_after_rejection}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Phase 36: single shared Forward Lead implementation (picker mode) */}
      <ForwardLeadDialog open={fwdOpen} onOpenChange={setFwdOpen} onForwarded={load} />

      {/* Accept confirm */}
      <Dialog open={!!acceptOf} onOpenChange={(v) => !v && setAcceptOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="fwd-accept-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Accept lead — {acceptOf?.fwd_code}</DialogTitle>
            <DialogDescription>
              Accepting locks this lead instantly, transfers ownership of the candidate's applications to you
              (existing member-ownership rules), and unlocks full candidate details. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={submitAccept} disabled={busy} className="w-full rounded-lg" data-testid="fwd-accept-confirm">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accept & Take Ownership
          </Button>
        </DialogContent>
      </Dialog>

      {/* Reason dialogs */}
      <ReasonDialog open={!!rejectOf} onClose={() => setRejectOf(null)} title={`Reject — ${rejectOf?.fwd_code}`}
        description="Each eligible recruiter gets one opportunity. If everyone rejects, the lead auto-closes."
        label="Rejection reason" required reason={reason} setReason={setReason} busy={busy}
        onSubmit={submitReject} testId="fwd-reject-dialog" submitTestId="fwd-reject-submit" destructive />
      <ReasonDialog open={!!withdrawOf} onClose={() => setWithdrawOf(null)} title={`Withdraw — ${withdrawOf?.fwd_code}`}
        description="Withdrawing removes this lead from all pools. Only possible before acceptance."
        label="Remarks (optional)" reason={reason} setReason={setReason} busy={busy}
        onSubmit={submitWithdraw} testId="fwd-withdraw-dialog" submitTestId="fwd-withdraw-submit" />
      <ReasonDialog open={!!closeOf} onClose={() => setCloseOf(null)} title={`Close — ${closeOf?.fwd_code}`}
        description="Closing is terminal and audited." label="Close reason" required
        reason={reason} setReason={setReason} busy={busy} onSubmit={submitClose}
        testId="fwd-close-dialog" submitTestId="fwd-close-submit" destructive />
      <ReasonDialog open={!!expireOf} onClose={() => setExpireOf(null)} title={`Mark Expired — ${expireOf?.fwd_code}`}
        description="Admin-only. Expired leads stay searchable and can be reopened later."
        label="Expiry remark" required reason={reason} setReason={setReason} busy={busy}
        onSubmit={submitExpire} testId="fwd-expire-dialog" submitTestId="fwd-expire-submit" />
      <ReasonDialog open={!!reopenOf} onClose={() => setReopenOf(null)} title={`Reopen — ${reopenOf?.fwd_code}`}
        description="Eligible recruiters are re-resolved from the current active allocation."
        label="Remarks (optional)" reason={reason} setReason={setReason} busy={busy}
        onSubmit={submitReopen} testId="fwd-reopen-dialog" submitTestId="fwd-reopen-submit" />
      <ReasonDialog open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Bulk close ${selected.length} lead(s)`}
        description="Applies one mandatory reason to all selected open leads." label="Close reason" required
        reason={reason} setReason={setReason} busy={busy} onSubmit={submitBulkClose}
        testId="fwd-bulk-dialog" submitTestId="fwd-bulk-submit" destructive />

      {/* Assign dialog (admin) */}
      <Dialog open={!!assignOf} onOpenChange={(v) => !v && setAssignOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="fwd-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{assignOf?.status === "Awaiting Allocation" ? "Assign" : "Reassign"} — {assignOf?.fwd_code}</DialogTitle>
            <DialogDescription>
              The lead becomes visible only to the chosen recruiter. M07 allocations are never modified automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Recruiter</Label>
              <Select value={assignRid} onValueChange={setAssignRid}>
                <SelectTrigger className="bg-white" data-testid="fwd-assign-select"><SelectValue placeholder="Choose recruiter" /></SelectTrigger>
                <SelectContent>
                  {recruiters.filter((r) => r.id !== assignOf?.from_recruiter_id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={submitAssign} disabled={busy} className="w-full rounded-lg" data-testid="fwd-assign-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Assign Lead
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailOf} onOpenChange={(v) => !v && setDetailOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="fwd-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detailOf?.fwd_code} — {detailOf?.candidate_name}</DialogTitle>
            <DialogDescription>
              {detailOf?.company_name} · {detailOf?.job_title || "Any suitable job"} · {detailOf?.status}
            </DialogDescription>
          </DialogHeader>
          {subLoading && <Skeleton className="h-40 w-full rounded-lg" />}
          {!subLoading && detail && (
            <div className="space-y-3">
              {detail.masked && (
                <p className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5 flex items-center gap-2" data-testid="fwd-masked-note">
                  <Lock className="h-3.5 w-3.5 shrink-0" /> {detail.masked_note}
                </p>
              )}
              <div className="rounded-lg border border-border p-3 space-y-1 text-sm" data-testid="fwd-candidate-preview">
                <p className="font-medium">{detail.candidate_preview?.name || detail.candidate_name}</p>
                <p className="text-xs text-muted-foreground">Location: {detail.candidate_preview?.location || "—"}
                  {detail.candidate_preview?.preferred_locations?.length ? ` · Prefers: ${detail.candidate_preview.preferred_locations.join(", ")}` : ""}</p>
                <p className="text-xs text-muted-foreground">Experience: {detail.candidate_preview?.experience || "—"}
                  {detail.candidate_preview?.current_company ? ` · ${detail.candidate_preview.current_designation || ""} @ ${detail.candidate_preview.current_company}` : ""}</p>
                {detail.candidate_preview?.languages?.length > 0 && (
                  <p className="text-xs text-muted-foreground">Languages: {detail.candidate_preview.languages.join(", ")}</p>
                )}
                {detail.candidate_preview?.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {detail.candidate_preview.skills.slice(0, 8).map((s) => (
                      <Badge key={s} variant="outline" className="rounded-full text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
                {!detail.masked && detail.candidate_contact && (
                  <p className="text-xs pt-1" data-testid="fwd-candidate-contact">
                    Contact: <span className="font-mono">{detail.candidate_contact.mobile || "—"}</span>
                    {detail.candidate_contact.email ? ` · ${detail.candidate_contact.email}` : ""}
                  </p>
                )}
              </div>
              <div className="rounded-lg border border-border p-3 text-xs space-y-1">
                <p>Reason: <span className="text-foreground">{detail.reason}</span></p>
                {detail.remarks && <p>Remarks: {detail.remarks}</p>}
                <p>Forwarded by: {detail.from_recruiter_name || detail.created_by_name} · {formatDateTime(detail.created_at)}</p>
                {detail.accepted_by_name && <p>Accepted by: {detail.accepted_by_name} · {formatDateTime(detail.accepted_at)}</p>}
                {detail.closure_reason && <p className="text-amber-700">Closure: {detail.closure_reason}</p>}
                {(detail.rejections || []).map((r) => (
                  <p key={r.at} className="text-red-600">Rejected by {r.recruiter_name}: {r.reason}</p>
                ))}
                {detail.ownership_transfer?.applications_transferred?.length > 0 && (
                  <p data-testid="fwd-transfer-summary">Ownership: {detail.ownership_transfer.previous_recruiter || "Unassigned"} → {detail.ownership_transfer.new_recruiter}
                    {" "}({detail.ownership_transfer.applications_transferred.join(", ")})</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Timeline dialog */}
      <Dialog open={!!timelineOf} onOpenChange={(v) => !v && setTimelineOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="fwd-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Candidate timeline — {timelineOf?.candidate_name}</DialogTitle>
            <DialogDescription>Application → Forward Lead → Accepted → Lineup → Interview → Employment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-0">
            {subLoading && <Skeleton className="h-32 w-full rounded-lg" />}
            {!subLoading && timeline?.events?.map((e, idx) => (
              <div key={idx} className="flex gap-3" data-testid="fwd-timeline-event">
                <div className="flex flex-col items-center">
                  <span className={`h-2.5 w-2.5 rounded-full mt-1.5 shrink-0 ${e.kind === "accepted" ? "bg-emerald-500" : e.kind === "forward" ? "bg-amber-400" : "bg-primary/60"}`} />
                  {idx < timeline.events.length - 1 && <span className="w-px flex-1 bg-border" />}
                </div>
                <div className="pb-4 min-w-0">
                  <p className="text-sm font-medium">{e.title}</p>
                  {e.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{e.subtitle}</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{formatDateTime(e.at)}{e.by ? ` · by ${e.by}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="fwd-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">History — {historyOf?.fwd_code}</DialogTitle>
            <DialogDescription>Immutable log of every action. Never edited or deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {subLoading && <Skeleton className="h-24 w-full rounded-lg" />}
            {!subLoading && history.map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="fwd-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {e.event_type.replaceAll("_", " ")}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {e.previous_status && e.new_status && (
                  <p className="text-sm mt-1.5">{e.previous_status}<span className="text-muted-foreground mx-1.5">→</span><span className="font-medium">{e.new_status}</span></p>
                )}
                {e.reason && <p className="text-xs text-amber-700 mt-1">Reason: {e.reason}</p>}
                {e.details?.previous_recruiter !== undefined && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {e.details.previous_recruiter || "Unassigned"} <span className="mx-1">→</span>
                    <span className="text-foreground">{e.details.new_recruiter}</span>
                    {e.details.applications_transferred?.length ? ` · ${e.details.applications_transferred.join(", ")}` : ""}
                  </p>
                )}
                {e.details?.assigned_recruiter && <p className="text-xs text-muted-foreground mt-1">Assigned to {e.details.assigned_recruiter}</p>}
                {e.remarks && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ReasonDialog = ({ open, onClose, title, description, label, required = false, reason, setReason,
  busy, onSubmit, testId, submitTestId, destructive = false }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="sm:max-w-md rounded-xl" data-testid={testId}>
      <DialogHeader>
        <DialogTitle className="font-heading">{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
          <Textarea rows={2} className="bg-white" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid={`${testId}-input`} />
        </div>
        <Button variant={destructive ? "destructive" : "default"} onClick={onSubmit} disabled={busy}
          className="w-full rounded-lg" data-testid={submitTestId}>
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
